/**
 * UX Sprint 2 — Stage hero card.
 *
 * Premium dark hero band at the top of every stage workspace:
 *   - Stage index + name
 *   - One-line purpose
 *   - Readiness chip
 *   - Primary action button (orange) + secondary actions
 *   - Last-updated meta (when available)
 */
import { ArrowRight, History } from "lucide-react";
import ReadinessPill from "./ReadinessPill";

export default function StageHeroCard({
    stageIndex,
    stageLabel,
    purpose,
    overallStatus = "not_started",
    primaryAction,    // { label, onClick, testid }
    secondaryActions = [], // [{ label, onClick, testid, icon?: Component }]
    lastUpdated,
}) {
    return (
        <section
            data-testid="stage-hero-card"
            className="relative overflow-hidden border border-zinc-800 rounded-sm bg-gradient-to-br from-[#0e0a08] via-[#0a0a0a] to-black"
        >
            {/* faint orange grain top-right */}
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FF4400]/8 blur-3xl" />
            <div className="relative px-6 lg:px-8 py-6 flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
                        <span className="text-[#FF4400]">{stageIndex ? `Stage ${String(stageIndex).padStart(2, "0")}` : "Stage"}</span>
                        <span className="text-zinc-700">·</span>
                        <ReadinessPill status={overallStatus} testid="stage-hero-status" />
                        {lastUpdated && (
                            <>
                                <span className="text-zinc-700">·</span>
                                <span className="inline-flex items-center gap-1 text-zinc-600">
                                    <History className="w-3 h-3" /> {lastUpdated}
                                </span>
                            </>
                        )}
                    </div>
                    <h2
                        data-testid="stage-hero-title"
                        className="font-display text-2xl sm:text-3xl tracking-tight text-white"
                    >
                        {stageLabel}
                    </h2>
                    {purpose && (
                        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">{purpose}</p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {primaryAction && (
                        <button
                            type="button"
                            onClick={primaryAction.onClick}
                            data-testid={primaryAction.testid || "stage-hero-primary"}
                            className="btn-primary text-xs px-4 py-2.5 inline-flex items-center gap-1.5"
                        >
                            {primaryAction.label}
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {secondaryActions.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <button
                                key={a.testid || a.label || i}
                                type="button"
                                onClick={a.onClick}
                                data-testid={a.testid}
                                className="btn-secondary text-xs px-3 py-2.5 inline-flex items-center gap-1.5"
                            >
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                                {a.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
